import React, { useRef } from 'react';
import { Rect, Group, Text } from 'react-konva';
import { Node } from '../../core/models/scene';
import { useThemeAwareColor } from '../../react-app/theme/ThemeProvider';

interface FrameNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
}

export const FrameNode: React.FC<FrameNodeProps> = ({
  node,
  selected,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick
}) => {
  const groupRef = useRef<any>(null);
  const getThemeColor = useThemeAwareColor();

  const handleDragStart = (e: any) => {
    const pos = e.target.position();
    onDragStart?.({ x: pos.x, y: pos.y });
  };

  const handleDragMove = (e: any) => {
    const pos = e.target.position();
    onDragMove?.({ x: pos.x, y: pos.y });
  };

  const handleDragEnd = (e: any) => {
    const pos = e.target.position();
    onDragEnd?.({ x: pos.x, y: pos.y });
  };

  // Accept either `width`/`height` (current model) or the deprecated
  // `w`/`h` fields. The Compass Explorer's synthesised frames emit
  // `w`/`h` via the scene builder, and the existing scene model
  // marks `w`/`h` as deprecated but still present.
  const width = node.width ?? (node as any).w ?? 400;
  const height = node.height ?? (node as any).h ?? 300;
  const frameType = node.attrs?.frameType || 'basic';
  const title = (node.attrs?.title as string | undefined) ?? node.name;

  // Different styles for different frame types
  const getFrameStyle = () => {
    switch (frameType) {
      case 'section':
        return {
          fill: 'rgba(156, 39, 176, 0.05)',
          stroke: '#9c27b0',
          strokeWidth: 2,
          cornerRadius: 8
        };
      case 'board':
        return {
          fill: 'rgba(33, 150, 243, 0.05)',
          stroke: '#2196f3',
          strokeWidth: 2,
          cornerRadius: 4
        };
      case 'grid':
        return {
          fill: 'rgba(255, 152, 0, 0.05)',
          stroke: '#ff9800',
          strokeWidth: 1,
          dash: [5, 5],
          cornerRadius: 0
        };
      case 'layers':
        return {
          fill: 'rgba(76, 175, 80, 0.05)',
          stroke: '#4caf50',
          strokeWidth: 2,
          cornerRadius: 12
        };
      default: // basic
        return {
          fill: getThemeColor(node.fillColor || 'rgba(0, 0, 0, 0.02)', { type: 'background' }),
          stroke: getThemeColor(node.strokeColor || '#E5E7EB', { type: 'foreground' }),
          strokeWidth: node.strokeWidth || 1,
          cornerRadius: 4,
          titleBar: true,
        } as const;
    }
  };

  const HEADER_H = 24;
  const style = getFrameStyle();
  const showTitleBar = Boolean(title) && (frameType === 'basic' || (style as any).titleBar);

  return (
    <Group
      ref={groupRef}
      x={node.x}
      y={node.y}
      draggable
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={onClick}
      onDblClick={onDoubleClick}
    >
      {/* Frame background */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={style.fill}
        stroke={style.stroke}
        strokeWidth={style.strokeWidth}
        dash={style.dash}
        cornerRadius={style.cornerRadius}
      />
      
      {/* Title bar for section frames */}
      {frameType === 'section' && (
        <>
          <Rect
            x={0}
            y={0}
            width={width}
            height={30}
            fill={style.stroke}
            opacity={0.1}
          />
          <Text
            x={10}
            y={8}
            text={title || 'Section'}
            fontSize={14}
            fontFamily="Noto Sans"
            fill={style.stroke || '#9c27b0'}
            fontStyle="bold"
          />
        </>
      )}

      {/* Basic-type title bar (opt-in via attrs.title or node.name).
          Used by the Compass Explorer's VPC / subnet / tier-group
          frames — the header bar + label makes the enclosing container
          legible at any zoom without needing the zoom-stable overlay.
          Opacity 0.25 reads clearly against the page while still
          letting the stroke colour show through as an accent. */}
      {frameType !== 'section' && showTitleBar && title && (
        <>
          <Rect
            x={0}
            y={0}
            width={width}
            height={HEADER_H}
            fill={style.stroke}
            opacity={0.25}
            cornerRadius={[style.cornerRadius, style.cornerRadius, 0, 0]}
          />
          <Text
            x={10}
            y={6}
            text={title}
            fontSize={12}
            fontFamily="Noto Sans"
            // Title text sits on a tinted stroke band; pick a dark
            // neutral so it stays readable regardless of the stroke
            // colour (light subnet tints would otherwise bleach it out).
            fill="#1e293b"
            fontStyle="bold"
            listening={false}
            ellipsis
            wrap="none"
            width={Math.max(0, width - 20)}
          />
        </>
      )}
      
      {/* Selection outline */}
      {selected && (
        <Rect
          x={-2}
          y={-2}
          width={width + 4}
          height={height + 4}
          stroke={getThemeColor('#3478DD', { type: 'accent' })}
          strokeWidth={2}
          fill="transparent"
          cornerRadius={style.cornerRadius}
        />
      )}
    </Group>
  );
};
